/* eslint-disable no-unused-vars */
import React, { ReactNode } from 'react';
import { Toast as ToastPrimitive } from '@base-ui/react/toast';
import { Box, Icon, chakra } from '@chakra-ui/react';
import { CheckCircle, Info, Warning, WarningOctagon, X } from 'phosphor-react';

export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export type ToastStatus = 'success' | 'error' | 'warning' | 'info' | 'loading';

export const toastManager = ToastPrimitive.createToastManager();
export const anchoredToastManager = ToastPrimitive.createToastManager();

const statusTone: Record<Exclude<ToastStatus, 'loading'>, { icon: any; color: string }> = {
  success: { icon: CheckCircle, color: 'state.success' },
  error: { icon: WarningOctagon, color: 'state.destructive' },
  warning: { icon: Warning, color: 'state.warning' },
  info: { icon: Info, color: 'state.link' },
};

const viewportPlacement: Record<ToastPosition, Record<string, string>> = {
  'top-left': { top: '16px', left: '16px', alignItems: 'flex-start' },
  'top-center': { top: '16px', left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
  'top-right': { top: '16px', right: '16px', alignItems: 'flex-end' },
  'bottom-left': { bottom: '16px', left: '16px', alignItems: 'flex-start' },
  'bottom-center': {
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    alignItems: 'center',
  },
  'bottom-right': { bottom: '16px', right: '16px', alignItems: 'flex-end' },
};

const Viewport = chakra(ToastPrimitive.Viewport);
const Root = chakra(ToastPrimitive.Root);
const Title = chakra(ToastPrimitive.Title);
const Description = chakra(ToastPrimitive.Description);
const Close = chakra(ToastPrimitive.Close);
const Action = chakra(ToastPrimitive.Action);

export const ToastTitle = ({ children }: { children?: ReactNode }) => (
  <Title fontSize="13px" lineHeight="20px" fontWeight={500} color="content.strong">
    {children}
  </Title>
);

export const ToastDescription = ({ children }: { children?: ReactNode }) => (
  <Description fontSize="13px" lineHeight="20px" color="content.body">
    {children}
  </Description>
);

export const ToastAction = ({ children, ...rest }: any) => (
  <Action
    h="30px"
    px="10px"
    mt="8px"
    borderRadius="full"
    border="1px solid"
    borderColor="line.input"
    bg="surface.base"
    fontSize="13px"
    fontWeight={500}
    color="content.strong"
    cursor="pointer"
    _hover={{ bg: 'surface.selected' }}
    {...rest}
  >
    {children}
  </Action>
);

export const ToastClose = () => (
  <Close
    aria-label="Fechar aviso"
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="24px"
    h="24px"
    flexShrink={0}
    borderRadius="lg"
    color="content.subtle"
    cursor="pointer"
    _hover={{ bg: 'surface.selected', color: 'content.strong' }}
  >
    <X size={12} />
  </Close>
);

export const Toast = ({
  toast,
  position = 'top-right',
}: {
  toast: any;
  position?: ToastPosition;
}) => {
  const status = (toast.type ?? 'info') as ToastStatus;
  const tone = statusTone[status as Exclude<ToastStatus, 'loading'>] ?? statusTone.info;
  const fromTop = position.startsWith('top');
  const offset = fromTop ? 'var(--toast-offset-y)' : 'calc(var(--toast-offset-y) * -1)';
  const enterFrom = fromTop ? '-12px' : '12px';

  return (
    <Root
      toast={toast}
      position="absolute"
      top={fromTop ? 0 : 'auto'}
      bottom={fromTop ? 'auto' : 0}
      right={0}
      left="auto"
      display="flex"
      gap="10px"
      w="360px"
      maxW="calc(100vw - 32px)"
      p="12px"
      bg="surface.base"
      borderRadius="xl"
      boxShadow="popover"
      pointerEvents="auto"
      sx={{
        zIndex: 'calc(1000 - var(--toast-index))',
        transform: `translateY(calc(${offset} + var(--toast-swipe-movement-y))) scale(calc(1 - var(--toast-index) * 0.05))`,
        transition: 'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), opacity 250ms ease',
        '&[data-expanded]': {
          transform: `translateY(calc(${offset} + var(--toast-swipe-movement-y))) scale(1)`,
        },
        '&[data-starting-style], &[data-ending-style]': {
          opacity: 0,
          transform: `translateY(${enterFrom}) scale(0.98)`,
        },
        '&[data-limited]': { opacity: 0 },
      }}
    >
      {status !== 'loading' && (
        <Icon as={tone.icon} w="16px" h="16px" mt="2px" flexShrink={0} color={tone.color} />
      )}
      <Box flex="1" minW={0}>
        <ToastTitle>{toast.title}</ToastTitle>
        {toast.description && <ToastDescription>{toast.description}</ToastDescription>}
        {toast.actionProps && <ToastAction {...toast.actionProps} />}
      </Box>
      <ToastClose />
    </Root>
  );
};

export const ToastViewport = ({ position = 'top-right' }: { position?: ToastPosition }) => {
  const { toasts } = ToastPrimitive.useToastManager();

  return (
    <Viewport
      position="fixed"
      display="flex"
      flexDirection="column"
      minH={toasts.length ? '80px' : '0'}
      w="360px"
      maxW="calc(100vw - 32px)"
      zIndex={1700}
      pointerEvents="none"
      sx={viewportPlacement[position]}
    >
      {toasts.map((toast: any) => (
        <Toast key={toast.id} toast={toast} position={position} />
      ))}
    </Viewport>
  );
};

export const ToastProvider = ({
  children,
  position = 'top-right',
}: {
  children: ReactNode;
  position?: ToastPosition;
}) => (
  <ToastPrimitive.Provider toastManager={toastManager}>
    {children}
    <ToastPrimitive.Portal>
      <ToastViewport position={position} />
    </ToastPrimitive.Portal>
  </ToastPrimitive.Provider>
);

export const AnchoredToastProvider = ({ children }: { children: ReactNode }) => (
  <ToastPrimitive.Provider toastManager={anchoredToastManager}>
    {children}
    <ToastPrimitive.Portal>
      <AnchoredToasts />
    </ToastPrimitive.Portal>
  </ToastPrimitive.Provider>
);

const AnchoredToasts = () => {
  const { toasts } = ToastPrimitive.useToastManager();

  return (
    <>
      {toasts.map((toast: any) => (
        <ToastPrimitive.Positioner key={toast.id} {...(toast.positionerProps ?? {})}>
          <Root
            toast={toast}
            display="flex"
            gap="8px"
            alignItems="center"
            px={toast.data?.tooltipStyle ? '8px' : '12px'}
            py={toast.data?.tooltipStyle ? '4px' : '12px'}
            bg={toast.data?.tooltipStyle ? 'content.strong' : 'surface.base'}
            color={toast.data?.tooltipStyle ? 'content.inverse' : 'content.strong'}
            borderRadius={toast.data?.tooltipStyle ? 'sm' : 'xl'}
            boxShadow={toast.data?.tooltipStyle ? 'none' : 'popover'}
            fontSize="12px"
          >
            {toast.data?.tooltipStyle ? (
              <ToastPrimitive.Title>{toast.title}</ToastPrimitive.Title>
            ) : (
              <Box>
                <ToastTitle>{toast.title}</ToastTitle>
                {toast.description && <ToastDescription>{toast.description}</ToastDescription>}
              </Box>
            )}
          </Root>
        </ToastPrimitive.Positioner>
      ))}
    </>
  );
};

interface LegacyToastOptions {
  id?: string;
  title?: ReactNode;
  description?: ReactNode;
  status?: ToastStatus;
  duration?: number | null;
  isClosable?: boolean;
  position?: string;
  variant?: string;
}

const toText = (value: ReactNode): string | undefined => {
  if (value == null) return undefined;
  return typeof value === 'string' ? value : String(value);
};

export const showToast = ({ id, title, description, status, duration }: LegacyToastOptions) =>
  toastManager.add({
    id,
    title: toText(title) ?? '',
    description: toText(description),
    type: status ?? 'info',
    timeout: duration === null ? 0 : duration ?? 5000,
  });

export const useToast = () => {
  const call = (options: LegacyToastOptions) => showToast(options);
  call.close = (id: string) => toastManager.close(id);
  call.update = (id: string, options: LegacyToastOptions) => showToast({ ...options, id });
  return call;
};

export type { LegacyToastOptions as UseToastOptions };
